import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "@/components/layout/MainLayout";
import { FeedCard, FeedPrompt } from "@/components/feed/FeedCard";
import { PromptDetailModal } from "@/components/shared/PromptDetailModal";
import { RemixersModal } from "@/components/feed/RemixersModal";
import { FeedFilters, FeedFiltersState } from "@/components/feed/FeedFilters";
import FeedPromptInput from "@/components/feed/FeedPromptInput";
import { FeedRightRail } from "@/components/feed/FeedRightRail";
import { FeedPromptOfDay } from "@/components/feed/FeedPromptOfDay";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, Pencil, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
interface Remixer {
  user_id: string;
  user_initials: string;
}

interface PromptDetailData {
  score_id: string;
  overall_score: number;
  original_score?: number | null;
  detected_type: string | null;
  best_prompt_extracted: string | null;
  dimension_scores_json: Record<string, any> | null;
  evaluation_markdown: string | null;
  chatcraft_subscores_json: Record<string, number> | null;
  original_content: string | null;
  submission_type: string | null;
  points_total: number | null;
  points_base?: number | null;
  points_rubric?: number | null;
  points_bonus?: number | null;
  user_name?: string | null;
  created_at: string;
  tags?: string[];
  submission_id?: string | null;
  is_public?: boolean | null;
  user_id?: string | null;
}

export default function PromptFeed() {
  const { membership, user, profileName } = useAuth();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<FeedPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filter state
  const [filters, setFilters] = useState<FeedFiltersState>({
    scoreRange: "all",
    promptTypes: [],
    tags: [],
    engagementTypes: [],
    sortOrder: "desc"
  });

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [promptDetail, setPromptDetail] = useState<PromptDetailData | null>(null);

  // Remixers modal state
  const [showRemixersModal, setShowRemixersModal] = useState(false);
  const [remixersPrompt, setRemixersPrompt] = useState<FeedPrompt | null>(null);


  const loadMoreRef = useRef<HTMLDivElement>(null);
  const ITEMS_PER_PAGE = 20;

  // Collect unique tags from all loaded prompts
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    prompts.forEach((p) => {
      p.tags?.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [prompts]);

  // Apply filters to prompts
  const filteredPrompts = useMemo(() => {
    let result = [...prompts];

    // Score filter
    if (filters.scoreRange !== "all") {
      result = result.filter((p) => {
        switch (filters.scoreRange) {
          case "85+":return p.score_total >= 85;
          case "70-84":return p.score_total >= 70 && p.score_total < 85;
          case "50-69":return p.score_total >= 50 && p.score_total < 70;
          case "<50":return p.score_total < 50;
          default:return true;
        }
      });
    }

    // Prompt type filter (match any selected)
    if (filters.promptTypes.length > 0) {
      result = result.filter((p) =>
      filters.promptTypes.includes(p.detected_type)
      );
    }

    // Tag filter (match any selected)
    if (filters.tags.length > 0) {
      result = result.filter((p) =>
      p.tags?.some((tag) => filters.tags.includes(tag))
      );
    }

    // Engagement filter (match any selected)
    if (filters.engagementTypes.length > 0) {
      result = result.filter((p) => {
        if (filters.engagementTypes.includes("favorited") && p.favorite_count > 0) return true;
        if (filters.engagementTypes.includes("remixed") && p.remix_count > 0) return true;
        return false;
      });
    }

    // Priority-based sort logic
    result.sort((a, b) => {
      const multiplier = filters.sortOrder === "desc" ? -1 : 1;

      // Priority 1: Score Range (if not "all") - sort by score
      if (filters.scoreRange !== "all") {
        return multiplier * (a.score_total - b.score_total);
      }

      // Priority 2: Engagement (if any selected, Score Range is "all")
      if (filters.engagementTypes.length > 0) {
        // Favorites is the "lead" if both selected or only favorites
        if (filters.engagementTypes.includes("favorited")) {
          return multiplier * (a.favorite_count - b.favorite_count);
        }
        // Only remixes selected
        if (filters.engagementTypes.includes("remixed")) {
          return multiplier * (a.remix_count - b.remix_count);
        }
      }

      // Priority 3: Chronological (default)
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return multiplier * (dateA - dateB);
    });

    return result;
  }, [prompts, filters]);

  const fetchPrompts = useCallback(async (beforeTimestamp?: string) => {
    if (!membership?.org_id) return [];

    const { data, error } = await supabase.rpc('get_org_prompt_feed', {
      _org_id: membership.org_id,
      _limit: ITEMS_PER_PAGE,
      _before_timestamp: beforeTimestamp || null
    });

    if (error) {
      console.error('Error fetching feed:', error);
      return [];
    }

    // Map the data to properly type recent_remixers
    return (data || []).map((item: any) => ({
      ...item,
      recent_remixers: (item.recent_remixers || []) as Remixer[]
    })) as FeedPrompt[];
  }, [membership?.org_id]);

  // Initial load
  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);
      const data = await fetchPrompts();
      setPrompts(data);
      setHasMore(data.length === ITEMS_PER_PAGE);
      setLoading(false);
    };

    if (membership?.org_id) {
      loadInitial();
    }
  }, [membership?.org_id, fetchPrompts]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || prompts.length === 0) return;

    setLoadingMore(true);
    const lastPrompt = prompts[prompts.length - 1];
    const newData = await fetchPrompts(lastPrompt.created_at);

    if (newData.length > 0) {
      setPrompts((prev) => [...prev, ...newData]);
    }
    setHasMore(newData.length === ITEMS_PER_PAGE);
    setLoadingMore(false);
  }, [loadingMore, hasMore, prompts, fetchPrompts]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, loadingMore, loading, loadMore]);

  // Refresh function
  const handleRefresh = async () => {
    setRefreshing(true);
    const data = await fetchPrompts();
    setPrompts(data);
    setHasMore(data.length === ITEMS_PER_PAGE);
    setRefreshing(false);
  };

  // View prompt detail
  const handleViewDetail = async (prompt: FeedPrompt) => {
    if (!membership?.org_id) return;

    setShowDetailModal(true);
    setDetailLoading(true);
    setPromptDetail(null);

    try {
      const { data, error } = await supabase.rpc('get_prompt_detail_for_org', {
        _score_id: prompt.score_id,
        _org_id: membership.org_id
      });

      if (error) {
        console.error('Error fetching prompt detail:', error);
        return;
      }

      if (data && data.length > 0) {
        const detail = data[0];
        setPromptDetail({
          score_id: detail.score_id,
          overall_score: detail.overall_score,
          original_score: detail.original_score,
          detected_type: detail.detected_type,
          best_prompt_extracted: detail.best_prompt_extracted,
          dimension_scores_json: detail.dimension_scores_json as Record<string, any>,
          evaluation_markdown: detail.evaluation_markdown,
          chatcraft_subscores_json: detail.chatcraft_subscores_json as Record<string, number>,
          original_content: detail.original_content,
          submission_type: detail.submission_type,
          points_total: detail.points_total,
          points_base: detail.points_base,
          points_rubric: detail.points_rubric,
          points_bonus: detail.points_bonus,
          user_name: detail.user_name,
          created_at: detail.created_at,
          tags: prompt.tags,
          submission_id: detail.submission_id,
          is_public: detail.is_public,
          user_id: detail.user_id
        });
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setPromptDetail(null);
  };

  // Remix handler - navigate to builder page
  const handleRemix = (prompt: FeedPrompt) => {
    if (!prompt.best_prompt) {
      toast.error('Cannot remix this prompt');
      return;
    }
    navigate('/build/new', {
      state: {
        remixContext: { originalPrompt: prompt.best_prompt, originalScoreId: prompt.score_id },
        returnTo: '/feed',
      },
    });
  };

  const handleViewRemixers = (prompt: FeedPrompt) => {
    setRemixersPrompt(prompt);
    setShowRemixersModal(true);
  };

  const handleCloseRemixersModal = () => {
    setShowRemixersModal(false);
    setRemixersPrompt(null);
  };

  // New prompt builder handler - navigate to builder page
  const handleBuildPrompt = (goal: string) => {
    navigate('/build/new', { state: { initialGoal: goal, returnTo: '/feed' } });
  };

  // Get user initials for avatar using shared utility
  const userInitials = useMemo(() => {
    if (profileName) {
      const words = profileName.trim().split(/\s+/).filter((w) => w.length > 0);
      if (words.length >= 2) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
      }
      return words[0]?.[0]?.toUpperCase() || "U";
    }
    // Fallback to email first char if no profile name
    return user?.email?.charAt(0).toUpperCase() || "U";
  }, [profileName, user?.email]);

  return (
    <MainLayout>
      <div className="flex w-full min-w-0 overflow-x-hidden">
        {/* Main Feed - centered, unchanged width */}
        <div className="flex-1 flex justify-center min-w-0">
          <div className="w-full max-w-2xl px-0 pt-[10px]">
            {/* Header with inline input */}
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border/50 px-4 py-4 w-full">
              <FeedPromptInput
                userInitials={userInitials}
                onBuildPrompt={handleBuildPrompt}
                filterControls={
                <>
                    <FeedFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    availableTags={availableTags} />
                  
                    <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRefresh}
                    disabled={refreshing || loading}>
                    
                      <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  </>
                } />
              
            </div>

            {/* CosmoIdeas carousel - hidden, kept for future use */}
            {/* <CosmoIdeas
                     layout="carousel"
                     onBuildPrompt={(initialGoal) => {
                       navigate('/prompt', {
                         state: { remixContext: { originalPrompt: initialGoal } }
                       });
                     }}
                    /> */}

            {!loading && filteredPrompts.length > 0 &&
            <div className="flex items-center gap-2 px-4 pb-0 pt-[10px]">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="font-semibold text-foreground" style={{ fontSize: 22 }}>
                  Trending prompts
                </span>
              </div>
            }
            <div className="divide-y divide-border/30">
              {loading ?
              // Loading skeletons
              Array.from({ length: 5 }).map((_, i) =>
              <div key={i} className="p-4 md:p-5">
                    <div className="flex items-start gap-3 md:gap-4">
                      <Skeleton className="h-10 w-10 md:h-11 md:w-11 rounded-full flex-shrink-0" />
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-4 w-16" />
                        </div>
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-5 w-20 rounded-full" />
                          <Skeleton className="h-4 w-12" />
                        </div>
                        <Skeleton className="h-20 w-full" />
                        <Skeleton className="h-12 w-full rounded-lg" />
                        <div className="flex gap-2">
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                        </div>
                      </div>
                    </div>
                  </div>
              ) :
              filteredPrompts.length === 0 ?
              // Empty state
              <div className="p-8 text-center">
                  <div className="h-12 w-12 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
                    <Pencil className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">
                    {prompts.length === 0 ? "No prompts yet" : "No matching prompts"}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {prompts.length === 0 ?
                  "Be the first to create a prompt and share it with your team!" :
                  "Try adjusting your filters to see more prompts."}
                  </p>
                </div> :

              // Feed items with Prompt of the Day inserted at position 3 on mobile/tablet
              <>
                  {filteredPrompts.map((prompt, index) =>
                <div key={prompt.score_id}>
                      {/* Insert Prompt of the Day after the 2nd item (at position 3) - only on mobile/tablet */}
                      {index === 2 &&
                  <div className="xl:hidden border-b border-border/30">
                          <FeedPromptOfDay />
                        </div>
                  }
                      <FeedCard
                    prompt={prompt}
                    onViewDetail={handleViewDetail}
                    onRemix={handleRemix}
                    onViewRemixers={handleViewRemixers}
                    onHidePrompt={(p) => setPrompts((prev) => prev.filter((x) => x.score_id !== p.score_id))}
                    onDeletePrompt={(p) => setPrompts((prev) => prev.filter((x) => x.score_id !== p.score_id))} />
                  
                    </div>
                )}
                  {/* If fewer than 3 items, show Prompt of the Day at the end on mobile/tablet */}
                  {filteredPrompts.length > 0 && filteredPrompts.length < 3 &&
                <div className="xl:hidden border-b border-border/30">
                      <FeedPromptOfDay />
                    </div>
                }
                </>
              }
            </div>

            {/* Load more sentinel */}
            {!loading && hasMore &&
            <div ref={loadMoreRef} className="p-4 flex justify-center">
                {loadingMore &&
              <div className="flex items-center gap-2 text-muted-foreground">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Loading more...</span>
                  </div>
              }
              </div>
            }

            {/* End of feed */}
            {!loading && !hasMore && prompts.length > 0 &&
            <div className="p-6 text-center text-sm text-muted-foreground border-t border-border/30">
                You've reached the end of the feed
              </div>
            }
          </div>
        </div>

        {/* Right Rail - desktop only (≥1280px) */}
        <aside className="hidden xl:block w-96 flex-shrink-0 pl-6 pr-4 pt-4 border-l border-border/50">
          <FeedRightRail />
        </aside>
      </div>

      {/* Detail Modal */}
      <PromptDetailModal
        isOpen={showDetailModal}
        onClose={handleCloseDetailModal}
        data={promptDetail}
        loading={detailLoading}
        showUserName={true}
        onHidePrompt={() => {
          if (promptDetail) {
            setPrompts((prev) => prev.filter((x) => x.score_id !== promptDetail.score_id));
          }
        }}
        onDeletePrompt={() => {
          if (promptDetail) {
            setPrompts((prev) => prev.filter((x) => x.score_id !== promptDetail.score_id));
          }
        }}
        onRemix={(detail) => {
          handleCloseDetailModal();
          handleRemix({
            score_id: detail.score_id,
            best_prompt: detail.best_prompt_extracted || '',
            score_total: detail.overall_score,
            detected_type: detail.detected_type || '',
            user_name: detail.user_name || '',
            user_initials: '',
            user_id: '',
            created_at: detail.created_at,
            favorite_count: 0,
            remix_count: 0,
            recent_remixers: [],
            evaluation_markdown: detail.evaluation_markdown || ''
          });
        }} />
      

      {/* Remixers Modal */}
      {remixersPrompt &&
      <RemixersModal
        isOpen={showRemixersModal}
        onClose={handleCloseRemixersModal}
        scoreId={remixersPrompt.score_id}
        totalCount={remixersPrompt.remix_count} />

      }

    </MainLayout>);

}
